import React, { useContext, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { Text, Input, FilterOptions } from "react_reusable_components";
import {
  DownloadOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";

import Button from "../../components/Button";
import useResponsive from "../../core/hooks/useResponsive";
import ListingHeaderLayout from "../../layouts/ListingHeaderLayout";
import {
  getAuditListingFilterData,
  getConditionalContent,
  getLocalizedMessage,
  getProductAndSubcategoryFromFilterData,
  getSelectedCountries,
  getSelectedFilterItem,
  getSelectedStatus,
} from "../../UtilityFunctions/utils";
import Styles from "./AuditListingTopContainer.module.scss";
import ThreeColumn from "../../core/layouts/ThreeColumn";
import variables from "../../themes/base/styles/variables";
import ProductCategoryService from "../../services/product-category-service";
import { ProductCategoryContext } from "../../globalContext/productCategory/productCategoryProvider";
import { GlobalLoaderContext } from "../../globalContext/globalLoader/globalLoaderProvider";
import { AuthContext } from "../../globalContext/auth/authProvider";
import { AuditContext } from "../../globalContext/ManageAudit/AuditProvider";
import { setAuditQuery } from "../../globalContext/ManageAudit/auditActions";
import { userRole } from "../../constants";

const UserListingTopContainerStyle = {
  secondSectionMiddleSectionStyle: {
    display: variables.dFlex,
    gap: variables.gapSmaller,
    flexDirection: "row",
  },
  countStyle: {
    backgroundColor: "rgba(220, 0, 50, 0.12)",
    color: variables.primaryBg,
    height: variables.closeIcon,
    padding: variables.paddingXXSmall,
    display: variables.dFlex,
    justifyContent: variables.alignCenter,
    alignItems: variables.alignCenter,
    borderRadius: variables.countTagBorderRadius,
    fontSize: variables.fontSizeSmallest,
    fontWeight: variables.fontWeightBold,
    marginLeft: variables.marginSmall,
  },
};

const AuditListingTopContainer = ({
  query,
  setQuery,
  setFilter,
  downloadAuditReportsHandler,
}) => {
  const intl = useIntl();
  const responsive = useResponsive();
  const navigate = useNavigate();
  const loaderDispatch = useContext(GlobalLoaderContext)[1];
  const [auditState, auditDispatch] = useContext(AuditContext);

  const [countriesList, setCountriesList] = useState();
  const [open, setOpen] = useState(false);
  const [filtersCount, setFiltersCount] = useState(0);
  const [authState] = useContext(AuthContext);

  const handleInputChange = (e) => {
    setQuery((prevQuery) => ({
      ...prevQuery,
      searchStr: e.target.value,
    }));
  };

  const [productCategoryState, productCategoryDispatch] = useContext(
    ProductCategoryContext
  );

  useEffect(() => {
    ProductCategoryService.getProductCategoryList(
      productCategoryDispatch,
      {},
      intl,
      loaderDispatch
    );
  }, []);

  useEffect(() => {
    if (authState?.currentRole?.id === userRole.ADMIN)
      ProductCategoryService.getCountries(intl).then((data) => {
        setCountriesList(data?.result);
      });
  }, [authState?.currentRole?.id]);

  const filterOptions = getAuditListingFilterData(
    productCategoryState?.productCategoryList?.result,
    countriesList
  );

  const onApplyFilter = (data) => {
    const [productCategory, subCategory] =
      getProductAndSubcategoryFromFilterData(
        data?.find((item) => item?.value === "Product & sub-product category")
      );
    const selectedStatuses = getSelectedStatus(
      data?.find((item) => item?.value === "Status")
    );

    const selectedCountries = getSelectedCountries(
      data?.find((item) => item?.value === "Country")
    );

    const currentAuditType = getSelectedFilterItem(
      data?.find((item) => item?.value === "Current audit type")
    );

    const auditCreationYear = getSelectedFilterItem(
      data?.find((item) => item?.value === "Audit creation year")
    );

    setFilter(() => ({
      productCategories: productCategory,
      subCategories: subCategory,
      countries: selectedCountries,
      status: selectedStatuses,
      currentAuditType,
      auditCreationYear: auditCreationYear.map((item) => item?.split("-")[0]),
    }));
    setOpen(false);
    auditDispatch(setAuditQuery({ ...auditState?.query, prevFilters: data }));
  };

  const onResetFilter = () => {
    setFiltersCount(0);
    setFilter({
      productCategories: [],
      subCategories: [],
      status: [],
      countries: [],
      currentAuditType: [],
      auditCreationYear: [],
    });
    auditDispatch(setAuditQuery({ ...auditState?.query, prevFilters: [] }));
    setOpen(false);
  };

  useEffect(() => {
    let count = getCountValue(auditState);
    setFiltersCount(count);
  }, [auditState?.query?.prevFilters]);

  return (
    <ListingHeaderLayout
      firstSection={
        <Text className={Styles["heading-text"]}>Manage Audit</Text>
      }
      secondSection={
        <ThreeColumn
          className={Styles["second-section-style"]}
          leftSectionStyle={{ flex: 1 }}
          leftSection={
            <Input
              className={`${Styles["input-style"]} ${
                Styles[!responsive?.isMd && "input-style-mobile"]
              }`}
              placeholder={getLocalizedMessage(
                intl,
                "auditListing.searchPlaceholder"
              )}
              prefix={
                <SearchOutlined className={Styles["search-icon-style"]} />
              }
              onChange={handleInputChange}
              value={query.searchStr}
            />
          }
          middleSectionStyle={
            UserListingTopContainerStyle.secondSectionMiddleSectionStyle
          }
          middleSection={
            <>
              <FilterOptions
                defaultSelectedValue={auditState?.query?.prevFilters}
                filterData={filterOptions}
                open={open}
                isMobile={!responsive.isMd}
                setOpen={setOpen}
                onReset={onResetFilter}
                onApply={onApplyFilter}
                mainContainerWidth={700}
                placement={getConditionalContent({
                  condition: responsive?.isMd,
                  trueValue: "bottomRight",
                  falseValue: "bottom",
                })}
                button={
                  <Button
                    icon={
                      <img
                        src="/icons/filter_list.svg"
                        className={Styles["filter-icon-style"]}
                        alt="Filter"
                      />
                    }
                    className={Styles["add-Filter"]}
                    type="text"
                    onClick={() => setOpen(true)}
                  >
                    {getConditionalContent({
                      condition: responsive.isSm,
                      trueValue: "Filters",
                    })}
                    {filtersCount > 0 && (
                      <span style={UserListingTopContainerStyle.countStyle}>
                        {`${filtersCount}`}
                      </span>
                    )}
                  </Button>
                }
              />
              <Button
                icon={
                  <DownloadOutlined className={Styles["plus-icon-style"]} />
                }
                className={Styles["add-Filter"]}
                type="text"
                onClick={downloadAuditReportsHandler}
              >
                {responsive.isSm &&
                  getLocalizedMessage(intl, "button.generateReport")}
              </Button>
            </>
          }
          rightSectionStyle={
            authState?.currentRole?.id !== userRole.CA
              ? { display: "none" }
              : {}
          }
          rightSection={
            authState?.currentRole?.id === userRole.CA && (
              <Button
                icon={<PlusOutlined className={Styles["plus-icon-style"]} />}
                className={Styles["add-button"]}
                onClick={() => navigate("/plan-audit")}
              >
                {getConditionalContent({
                  condition: responsive.isSm,
                  trueValue: "Plan Audit",
                })}
              </Button>
            )
          }
        />
      }
    />
  );
};

export default AuditListingTopContainer;

function getCountValue(auditState) {
  let count = 0;
  if (auditState?.query?.prevFilters?.length) {
    for (const obj of auditState?.query?.prevFilters) {
      for (const child of obj?.children) {
        if (child.isSelected) {
          count += 1;
          break; // Move to the next object since we found a selected child
        }
      }
    }
  }
  return count;
}
